import { FC, ReactNode, useEffect, useState } from 'react';
import { array, object, string } from 'yup';
import { FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { useBlocker } from 'react-router-dom';

import { Box, Button, ConfirmModal } from '@base-app';
import { EntryForm, InputType, ManualFormProps, SectionName } from './ManualForm.types';
import { LeavingPageModal } from '@builder';
import {
  StyledButtonContainer,
  StyledCustomContainer,
  StyledForm,
  StyledFormContainer,
  StyledFormField,
} from './ManualForm.styles';
import { GENETIC_COUNSELING_TRANSLATION } from '@/modules/genetic-counseling/constants';
import { useUserFieldsValidation } from '@/modules/shared/hooks/useUserFieldsValidation';
import { Variants } from '@/common/types';

import CustomInput from './components/CustomInput/CustomInput';
import CustomSelect from './components/CustomSelect/CustomSelect';
import EditCard from '../EditCard/EditCard';

const ManualForm: FC<ManualFormProps> = ({ data, hasClean, isLoading, onSubmit }) => {
  const { t } = useTranslation(GENETIC_COUNSELING_TRANSLATION);
  const { email, phoneNumber } = useUserFieldsValidation();
  const [askToClean, setAskToClean] = useState<boolean>(false);

  const [editPatientInfo, setEditPatientInfo] = useState<boolean>(false);
  const [editPatientService, setEditPatientService] = useState<boolean>(false);

  const schema = object().shape({
    [SectionName.DETAILS]: array().of(
      object().shape({
        target: string().when('required', (field, schema) => {
          return field ? schema.required(t('patient_profile.required')) : schema.notRequired();
        }),
      }),
    ),
    [SectionName.INFO]: array().of(
      object().shape({
        target: string()
          .when('required', (field, schema) => {
            return field ? schema.required(t('patient_profile.required')) : schema.notRequired();
          })
          .when('type', (field, schema) => {
            if (field === InputType.EMAIL) {
              return email;
            }

            if (field === InputType.PHONE) {
              return phoneNumber;
            }

            return schema;
          }),
      }),
    ),
    [SectionName.SERVICE]: array().of(
      object().shape({
        target: string().when('required', (field, schema) => {
          return field ? schema.required(t('patient_profile.required')) : schema.notRequired();
        }),
      }),
    ),
  });

  const methods = useForm({
    defaultValues: {
      [SectionName.DETAILS]: data.filter(({ section }) => section === SectionName.DETAILS),
      [SectionName.INFO]: data.filter(({ section }) => section === SectionName.INFO),
      [SectionName.SERVICE]: data.filter(({ section }) => section === SectionName.SERVICE),
    },
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
  });

  const {
    control,
    formState: { isDirty, isSubmitting, isValid, isSubmitted },
    handleSubmit,
    reset,
    resetField,
  } = methods;

  const { fields: detailsFields } = useFieldArray({
    control,
    name: SectionName.DETAILS,
  });

  const { fields: infoFields } = useFieldArray({
    control,
    name: SectionName.INFO,
  });

  const { fields: serviceFields } = useFieldArray({
    control,
    name: SectionName.SERVICE,
  });

  const blocker = useBlocker(isDirty && !isSubmitted);
  const isBlocked = blocker.state === 'blocked';

  const handleFormSubmit: SubmitHandler<any> = (values): void => {
    onSubmit?.(values.info);
  };

  const getCustomItem = (field: EntryForm, index: number, isEditing: boolean): ReactNode => {
    switch (field.type) {
      case InputType.SELECT:
        return <CustomSelect index={index} field={field} isEditing={isEditing} />;

      default:
        return <CustomInput index={index} field={field} isEditing={isEditing} />;
    }
  };

  const handleEditInfoCard = (): void => {
    setEditPatientInfo(!editPatientInfo);
  };

  const handleEditServiceCard = (): void => {
    setEditPatientService(!editPatientService);
  };

  const handleClickData = (): void => {
    if (!isDirty) {
      reset();
    } else {
      setAskToClean(true);
    }
  };

  const handleCancelClearForm = (): void => {
    setAskToClean(false);
  };

  const handleConfirmClearForm = (): void => {
    reset();
    setAskToClean(false);
  };

  useEffect(() => {
    if (isBlocked && !isDirty) {
      blocker.reset?.();
    }
  }, [blocker, isDirty]);

  useEffect(() => {
    if (!editPatientInfo) {
      resetField(SectionName.INFO);
    }

    if (!editPatientService) {
      resetField(SectionName.SERVICE);
    }
  }, [editPatientInfo, editPatientService]);

  return (
    <FormProvider {...methods}>
      <StyledForm data-testid={`custom-form`} onSubmit={handleSubmit(handleFormSubmit)}>
        <StyledFormContainer>
          <EditCard css={{ marginBottom: '$6' }} title={t('patient_profile.patient_details')}>
            {detailsFields?.map((field, index) => {
              return (
                <StyledFormField key={field.id}>
                  <StyledCustomContainer data-testid={`custom-form-container-${index}`}>
                    <Box>{getCustomItem(field, index, false)}</Box>
                  </StyledCustomContainer>
                </StyledFormField>
              );
            })}
          </EditCard>
          <EditCard
            css={{ marginBottom: '$6' }}
            handleEdit={handleEditInfoCard}
            isEditable={false}
            isEditing={editPatientInfo}
            title={t('patient_profile.contact_info')}
          >
            {infoFields?.map((field, index) => {
              return (
                <StyledFormField key={field.id}>
                  <StyledCustomContainer data-testid={`custom-form-container-${index}`}>
                    <Box>{getCustomItem(field, index, editPatientInfo)}</Box>
                  </StyledCustomContainer>
                </StyledFormField>
              );
            })}
          </EditCard>
          <EditCard
            css={{ marginBottom: '$6' }}
            isEditing={editPatientService}
            handleEdit={handleEditServiceCard}
            title={t('patient_profile.service_details')}
          >
            {serviceFields?.map((field, index) => {
              return (
                <StyledFormField key={field.id}>
                  <StyledCustomContainer data-testid={`custom-form-container-${index}`}>
                    <Box>{getCustomItem(field, index, editPatientService)}</Box>
                  </StyledCustomContainer>
                </StyledFormField>
              );
            })}
          </EditCard>
        </StyledFormContainer>
        <StyledButtonContainer>
          <Button
            css={{ width: '100%' }}
            id="custom-form-button"
            disabled={!(!isSubmitting && isValid)}
            type="submit"
            loading={isLoading}
          >
            {t('patient_profile.submit')}
          </Button>
          {hasClean && (
            <Button
              disabled={!isDirty}
              id="custom-form-clean"
              onClick={handleClickData}
              type="button"
              variant={Variants.TERTIARY}
            >
              {t('patient_profile.clear')}
            </Button>
          )}
        </StyledButtonContainer>
      </StyledForm>
      {isBlocked && (
        <LeavingPageModal
          confirmText={t('create.navigate_away_confirm_modal.confirm')}
          declineText={t('create.navigate_away_confirm_modal.decline')}
          description={t('create.navigate_away_confirm_modal.description')}
          isShowing={isBlocked}
          onCancel={() => blocker.reset?.()}
          onConfirm={() => blocker.proceed?.()}
          title={t('create.navigate_away_confirm_modal.title')}
        />
      )}
      {askToClean && (
        <ConfirmModal
          title={t('create.clear_patient_form.title')}
          cancelAction={{
            text: t('create.clear_patient_form.cancel'),
            onClick: handleCancelClearForm,
          }}
          confirmAction={{
            text: t('create.clear_patient_form.confirm'),
            onClick: handleConfirmClearForm,
          }}
          show={askToClean}
        />
      )}
    </FormProvider>
  );
};

export default ManualForm;
